import { useEffect, useMemo, useState, type FormEvent, type ReactElement } from 'react';
import { ApiError } from '../shared/api/HttpClient';
import type { TrackSummary } from '../segue/types';
import { SchedulerApi } from './SchedulerApi';
import { assignBaseMatrix, baseMatrixId, createStarterConfiguration, isBaseScheduleRule, normalizeClockSlots, separationExplanation, validateConfiguration } from './schedulerModel';
import type { ClockSlot, ClockSlotType, ClockTemplate, FutureSchedule, Program, RotationCategory, SchedulerConfiguration, ScheduleRule, StationSummary } from './types';

const weekdays = [
  [1, 'Пн'], [2, 'Вт'], [3, 'Ср'], [4, 'Чт'], [5, 'Пт'], [6, 'Сб'], [7, 'Нд'],
] as const;

type WorkspaceTab = 'schedule' | 'matrices' | 'queue';

export function SchedulerWorkspace(): ReactElement {
  const api = useMemo(() => new SchedulerApi(), []);
  const [stations, setStations] = useState<StationSummary[]>([]);
  const [stationId, setStationId] = useState('');
  const [configuration, setConfiguration] = useState<SchedulerConfiguration | null>(null);
  const [future, setFuture] = useState<FutureSchedule | null>(null);
  const [tracks, setTracks] = useState<TrackSummary[]>([]);
  const [activeTab, setActiveTab] = useState<WorkspaceTab>('schedule');
  const [selectedMatrixId, setSelectedMatrixId] = useState('');
  const [selectedItemId, setSelectedItemId] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [selectedRuleId, setSelectedRuleId] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [manualTrackId, setManualTrackId] = useState('');
  const [manualAt, setManualAt] = useState('');
  const [insertMode, setInsertMode] = useState<'manual' | 'exact'>('manual');
  const [exactTolerance, setExactTolerance] = useState(5000);

  useEffect(() => {
    let active = true;
    void api.stations().then((items) => {
      if (!active) return;
      setStations(items);
      const queryStation = new URLSearchParams(window.location.search).get('station');
      const initial = items.find((station) => station.id === queryStation)?.id ?? items[0]?.id ?? '';
      setStationId(initial);
      if (initial === '') setLoading(false);
    }).catch((reason: unknown) => {
      if (active) {
        setError(problemMessage(reason));
        setLoading(false);
      }
    });

    return () => { active = false; };
  }, [api]);

  useEffect(() => {
    if (stationId === '') return;
    let active = true;
    setLoading(true);
    setError(null);
    setNotice(null);
    void Promise.all([api.configuration(stationId), api.future(stationId), api.tracks()]).then(([nextConfiguration, nextFuture, nextTracks]) => {
      if (!active) return;
      const firstMatrix = baseMatrixId(nextConfiguration) ?? nextConfiguration.clockTemplates[0]?.id ?? '';
      setConfiguration(nextConfiguration);
      setFuture(nextFuture);
      setTracks(nextTracks);
      setSelectedMatrixId(firstMatrix);
      setSelectedItemId(nextConfiguration.clockTemplates.find((matrix) => matrix.id === firstMatrix)?.slots[0]?.id ?? '');
      setSelectedCategoryId(nextConfiguration.rotationCategories[0]?.id ?? '');
      setSelectedRuleId(nextConfiguration.scheduleRules.find((rule) => !isBaseScheduleRule(rule))?.id ?? '');
      setDirty(false);
      setLoading(false);
    }).catch((reason: unknown) => {
      if (active) {
        setError(problemMessage(reason));
        setLoading(false);
      }
    });

    return () => { active = false; };
  }, [api, stationId]);

  const station = stations.find((item) => item.id === stationId) ?? null;
  const selectedMatrix = configuration?.clockTemplates.find((matrix) => matrix.id === selectedMatrixId) ?? null;
  const selectedItem = selectedMatrix?.slots.find((item) => item.id === selectedItemId) ?? null;
  const selectedCategory = configuration?.rotationCategories.find((category) => category.id === selectedCategoryId) ?? null;
  const selectedRule = configuration?.scheduleRules.find((rule) => rule.id === selectedRuleId) ?? null;
  const baseId = configuration === null ? null : baseMatrixId(configuration);
  const scheduledRules = configuration?.scheduleRules.filter((rule) => !isBaseScheduleRule(rule)) ?? [];
  const programByMatrix = new Map((configuration?.programs ?? []).map((program) => [program.defaultClockTemplateId, program]));

  function mutate(update: (current: SchedulerConfiguration) => SchedulerConfiguration): void {
    setConfiguration((current) => current === null ? null : update(current));
    setDirty(true);
    setNotice(null);
  }

  function updateMatrix(update: (matrix: ClockTemplate) => ClockTemplate): void {
    mutate((current) => {
      const clockTemplates = current.clockTemplates.map((matrix) => matrix.id === selectedMatrixId ? update(matrix) : matrix);
      const changed = clockTemplates.find((matrix) => matrix.id === selectedMatrixId);
      return {
        ...current,
        clockTemplates,
        programs: changed === undefined ? current.programs : current.programs.map((program) => program.defaultClockTemplateId === changed.id ? { ...program, name: changed.name } : program),
      };
    });
  }

  function updateItem(patch: Partial<ClockSlot>): void {
    updateMatrix((matrix) => ({
      ...matrix,
      slots: normalizeClockSlots(matrix.slots.map((item) => item.id === selectedItemId ? { ...item, ...patch } : item)),
    }));
  }

  function updateCategory(patch: Partial<RotationCategory>): void {
    mutate((current) => ({
      ...current,
      rotationCategories: current.rotationCategories.map((category) => category.id === selectedCategoryId ? { ...category, ...patch } : category),
    }));
  }

  function updateRule(patch: Partial<ScheduleRule>): void {
    mutate((current) => ({
      ...current,
      scheduleRules: current.scheduleRules.map((rule) => rule.id === selectedRuleId ? { ...rule, ...patch } : rule),
    }));
  }

  function addCategory(): void {
    if (configuration === null) return;
    const id = crypto.randomUUID();
    const number = configuration.rotationCategories.length + 1;
    mutate((current) => ({
      ...current,
      rotationCategories: [...current.rotationCategories, {
        id,
        code: `ROTATION_${number}`,
        name: `Ротація ${number}`,
        minTrackSeparationSeconds: 10_800,
        minArtistSeparationSeconds: 3_600,
        minCategorySeparationSeconds: 0,
        maxSameGenreSequence: 3,
        defaultWeight: 1,
        priority: 0,
        allowedDays: null,
        allowedHours: null,
        enabled: true,
        criteria: { itemTypes: ['TRACK'], artists: [], genres: [], languages: [], bpmMin: null, bpmMax: null },
      }],
    }));
    setSelectedCategoryId(id);
  }

  function addMatrix(asProgram = false): void {
    if (configuration === null) return;
    const id = crypto.randomUUID();
    const number = configuration.clockTemplates.length + 1;
    const matrix: ClockTemplate = { id, name: asProgram ? `Готовий плейлист ${number}` : `Матриця плейлистів ${number}`, enabled: true, slots: [] };
    mutate((current) => ({
      ...current,
      clockTemplates: [...current.clockTemplates, matrix],
      programs: asProgram ? [...current.programs, {
        id: crypto.randomUUID(),
        name: matrix.name,
        description: null,
        defaultClockTemplateId: id,
        enabled: true,
      }] : current.programs,
    }));
    setSelectedMatrixId(id);
    setSelectedItemId('');
    setActiveTab('matrices');
  }

  function addMatrixItem(): void {
    if (selectedMatrix === null || configuration === null) return;
    const id = crypto.randomUUID();
    const categoryId = configuration.rotationCategories[0]?.id ?? null;
    const isProgram = programByMatrix.has(selectedMatrix.id);
    const item: ClockSlot = {
      id,
      offsetSeconds: selectedMatrix.slots.length,
      slotType: isProgram ? 'ITEM' : categoryId === null ? 'FLEX' : 'ROTATION',
      rotationCategoryId: isProgram ? null : categoryId,
      fixedTrackId: isProgram ? '' : null,
      transitionProfileId: null,
      settings: {},
      position: selectedMatrix.slots.length,
    };
    updateMatrix((matrix) => ({ ...matrix, slots: normalizeClockSlots([...matrix.slots, item]) }));
    setSelectedItemId(id);
  }

  function removeMatrixItem(): void {
    if (selectedItem === null) return;
    updateMatrix((matrix) => ({ ...matrix, slots: normalizeClockSlots(matrix.slots.filter((item) => item.id !== selectedItem.id)) }));
    setSelectedItemId('');
  }

  function moveMatrixItem(direction: -1 | 1): void {
    if (selectedMatrix === null || selectedItem === null) return;
    const slots = normalizeClockSlots(selectedMatrix.slots);
    const index = slots.findIndex((item) => item.id === selectedItem.id);
    const destination = index + direction;
    if (index < 0 || destination < 0 || destination >= slots.length) return;
    [slots[index], slots[destination]] = [slots[destination]!, slots[index]!];
    updateMatrix((matrix) => ({ ...matrix, slots: normalizeClockSlots(slots.map((item, position) => ({ ...item, position }))) }));
  }

  function changeItemType(slotType: ClockSlotType): void {
    const categoryId = configuration?.rotationCategories[0]?.id ?? null;
    updateItem({
      slotType,
      rotationCategoryId: slotType === 'ROTATION' ? categoryId : null,
      fixedTrackId: slotType === 'ITEM' ? selectedItem?.fixedTrackId ?? '' : null,
    });
  }

  function addScheduleRule(): void {
    if (configuration === null) return;
    const matrixId = baseId ?? configuration.clockTemplates[0]?.id;
    if (matrixId === undefined) return;
    const id = crypto.randomUUID();
    const rule: ScheduleRule = {
      id,
      clockTemplateId: matrixId,
      programId: null,
      validFrom: null,
      validUntil: null,
      weekdays: [1, 2, 3, 4, 5],
      startLocal: '06:00:00',
      endLocal: '10:00:00',
      priority: 0,
      enabled: true,
    };
    mutate((current) => ({ ...current, scheduleRules: [...current.scheduleRules, rule] }));
    setSelectedRuleId(id);
    setActiveTab('schedule');
  }

  function removeScheduleRule(): void {
    if (selectedRule === null) return;
    mutate((current) => ({ ...current, scheduleRules: current.scheduleRules.filter((rule) => rule.id !== selectedRule.id) }));
    setSelectedRuleId('');
  }

  function setRuleSource(value: string): void {
    if (value.startsWith('program:')) updateRule({ programId: value.slice(8), clockTemplateId: null });
    else updateRule({ clockTemplateId: value.slice(7), programId: null });
  }

  function createStarter(): void {
    if (configuration === null) return;
    const starter = createStarterConfiguration(configuration.stationId, configuration.stationRevision);
    setConfiguration(starter);
    setSelectedMatrixId(starter.clockTemplates[0]?.id ?? '');
    setSelectedItemId(starter.clockTemplates[0]?.slots[0]?.id ?? '');
    setSelectedCategoryId(starter.rotationCategories[0]?.id ?? '');
    setSelectedRuleId('');
    setDirty(true);
  }

  async function save(): Promise<void> {
    if (configuration === null) return;
    const normalized = {
      ...configuration,
      clockTemplates: configuration.clockTemplates.map((matrix) => ({ ...matrix, slots: normalizeClockSlots(matrix.slots) })),
    };
    const validation = validateConfiguration(normalized);
    if (validation !== null) {
      setError(validation);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const saved = await api.save(normalized);
      setConfiguration(saved);
      setDirty(false);
      setNotice('Зміни збережено. Сервер перебудує лише майбутню, незаблоковану частину черги.');
      setFuture(await api.future(saved.stationId));
    } catch (reason: unknown) {
      setError(problemMessage(reason));
    } finally {
      setSaving(false);
    }
  }

  async function refreshFuture(): Promise<void> {
    if (stationId === '') return;
    try {
      setFuture(await api.future(stationId));
      setNotice('Чергу ефіру оновлено.');
    } catch (reason: unknown) {
      setError(problemMessage(reason));
    }
  }

  async function submitManualInsert(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (stationId === '' || manualTrackId === '' || manualAt === '') return;
    try {
      const scheduledAt = new Date(manualAt).toISOString();
      if (insertMode === 'exact') await api.createExactEvent(stationId, manualTrackId.trim(), scheduledAt, exactTolerance);
      else await api.createManualInsert(stationId, manualTrackId.trim(), scheduledAt);
      setManualTrackId('');
      setManualAt('');
      setNotice(insertMode === 'exact' ? 'Подію з точним часом додано.' : 'Ручну вставку додано.');
      setFuture(await api.future(stationId));
    } catch (reason: unknown) {
      setError(problemMessage(reason));
    }
  }

  return <main className="scheduler-workspace">
    <header className="scheduler-header">
      <div><p className="eyebrow">АВТОМАТИЧНИЙ ЕФІР</p><h1>Розклад ефіру</h1><p>Базова матриця заповнює вільний час, а проміжки розкладу тимчасово замінюють її іншою матрицею або готовим плейлистом.</p></div>
      <div className="scheduler-header-actions">
        <label className="station-picker"><span>Станція</span><select value={stationId} onChange={(event) => setStationId(event.target.value)}>{stations.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
        <button className="button secondary" type="button" onClick={() => void refreshFuture()}>Оновити чергу</button>
        <button className="button primary" type="button" disabled={!dirty || saving} onClick={() => void save()}>{saving ? 'Зберігаю…' : dirty ? 'Зберегти зміни' : 'Збережено'}</button>
      </div>
    </header>

    <section className="scheduler-status" aria-label="Стан автоматичного ефіру">
      <div><span className={`signal ${station?.enabled === true ? 'online' : ''}`} /><strong>{station?.enabled === true ? 'Станція активна' : 'Станція вимкнена'}</strong></div>
      <div><span>Черга наперед</span><strong>{station?.schedulerHorizonMinutes ?? 180} хв</strong></div>
      <div><span>Заблоковано</span><strong>найближчі 15 хв</strong></div>
      <div><span>Базова матриця</span><strong>{configuration?.clockTemplates.find((matrix) => matrix.id === baseId)?.name ?? 'не визначена'}</strong></div>
    </section>

    {error !== null && <div className="message error" role="alert"><span>ДІЮ НЕ ВИКОНАНО</span>{error}<button type="button" onClick={() => setError(null)}>Закрити</button></div>}
    {notice !== null && <div className="message success" role="status"><span>ГОТОВО</span>{notice}<button type="button" onClick={() => setNotice(null)}>Закрити</button></div>}
    {loading && <div className="workspace-loading">Завантажую розклад…</div>}
    {!loading && stations.length === 0 && <EmptyState title="Немає доступних станцій" detail="Спочатку створіть станцію." />}
    {!loading && configuration !== null && configuration.clockTemplates.length === 0 && <EmptyState title="Автоматичний ефір ще не налаштований" detail="Створимо базову матрицю, яка безперервно працюватиме в усіх вільних проміжках розкладу." action={<button className="button primary" type="button" onClick={createStarter}>Створити базову матрицю</button>} />}

    {!loading && configuration !== null && configuration.clockTemplates.length > 0 && <>
      <nav className="scheduler-tabs" aria-label="Розділи планування">
        <button className={activeTab === 'schedule' ? 'active' : ''} type="button" onClick={() => setActiveTab('schedule')}>Розклад</button>
        <button className={activeTab === 'matrices' ? 'active' : ''} type="button" onClick={() => setActiveTab('matrices')}>Матриці плейлистів</button>
        <button className={activeTab === 'queue' ? 'active' : ''} type="button" onClick={() => setActiveTab('queue')}>Черга на 3 години</button>
      </nav>

      {activeTab === 'schedule' && <ScheduleView configuration={configuration} baseId={baseId} rules={scheduledRules} selectedRule={selectedRule} onSelectRule={setSelectedRuleId} onSetBase={(matrixId) => mutate((current) => assignBaseMatrix(current, matrixId))} onAdd={addScheduleRule} onUpdate={updateRule} onRemove={removeScheduleRule} onSetSource={setRuleSource} />}
      {activeTab === 'matrices' && <MatricesView configuration={configuration} tracks={tracks} baseId={baseId} selectedMatrix={selectedMatrix} selectedItem={selectedItem} selectedCategory={selectedCategory} programByMatrix={programByMatrix} onSelectMatrix={(id) => { setSelectedMatrixId(id); setSelectedItemId(configuration.clockTemplates.find((matrix) => matrix.id === id)?.slots[0]?.id ?? ''); }} onSelectItem={setSelectedItemId} onSelectCategory={setSelectedCategoryId} onAddMatrix={() => addMatrix(false)} onAddProgram={() => addMatrix(true)} onAddCategory={addCategory} onAddItem={addMatrixItem} onUpdateMatrix={updateMatrix} onUpdateItem={updateItem} onChangeItemType={changeItemType} onUpdateCategory={updateCategory} onMoveItem={moveMatrixItem} onRemoveItem={removeMatrixItem} />}
      {activeTab === 'queue' && <QueueView future={future} tracks={tracks} stationId={stationId} manualTrackId={manualTrackId} manualAt={manualAt} insertMode={insertMode} exactTolerance={exactTolerance} onManualTrackId={setManualTrackId} onManualAt={setManualAt} onInsertMode={setInsertMode} onExactTolerance={setExactTolerance} onSubmit={submitManualInsert} />}
    </>}
  </main>;
}

function ScheduleView({ configuration, baseId, rules, selectedRule, onSelectRule, onSetBase, onAdd, onUpdate, onRemove, onSetSource }: { configuration: SchedulerConfiguration; baseId: string | null; rules: ScheduleRule[]; selectedRule: ScheduleRule | null; onSelectRule: (id: string) => void; onSetBase: (id: string) => void; onAdd: () => void; onUpdate: (patch: Partial<ScheduleRule>) => void; onRemove: () => void; onSetSource: (value: string) => void }): ReactElement {
  const matrixName = (id: string | null): string => configuration.clockTemplates.find((matrix) => matrix.id === id)?.name ?? 'Невідома матриця';
  const programName = (id: string | null): string => configuration.programs.find((program) => program.id === id)?.name ?? 'Невідомий плейлист';
  return <section className="schedule-layout">
    <div className="schedule-main">
      <section className="base-matrix-row">
        <div><p className="eyebrow">ЗАВЖДИ ПРАЦЮЄ У ВІЛЬНИЙ ЧАС</p><h2>Базова матриця</h2><p>Її не потрібно додавати до кожної години. Вона автоматично заповнює всі проміжки, для яких немає іншого правила.</p></div>
        <select value={baseId ?? ''} onChange={(event) => onSetBase(event.target.value)} aria-label="Базова матриця"><option value="" disabled>Оберіть базову матрицю…</option>{configuration.clockTemplates.filter((matrix) => !configuration.programs.some((program) => program.defaultClockTemplateId === matrix.id)).map((matrix) => <option key={matrix.id} value={matrix.id}>{matrix.name}</option>)}</select>
      </section>
      <header className="section-heading"><div><p className="eyebrow">ВИНЯТКИ З БАЗОВОГО ЕФІРУ</p><h2>Проміжки розкладу</h2></div><button className="button secondary" type="button" onClick={onAdd}>+ Додати проміжок</button></header>
      {rules.length === 0 ? <p className="schedule-empty">Проміжків ще немає. Увесь час працює базова матриця.</p> : <div className="schedule-rule-list">{rules.map((rule) => <button key={rule.id} type="button" className={rule.id === selectedRule?.id ? 'selected' : ''} onClick={() => onSelectRule(rule.id)}><span className={rule.enabled ? 'rule-state on' : 'rule-state'}>{rule.enabled ? 'АКТИВНИЙ' : 'ВИМКНЕНО'}</span><strong>{rule.programId === null ? matrixName(rule.clockTemplateId) : programName(rule.programId)}</strong><span>{weekdayLabel(rule.weekdays)} · {shortTime(rule.startLocal)}–{shortTime(rule.endLocal)}</span><small>{rule.validFrom === null && rule.validUntil === null ? 'Без обмеження за датою' : `${rule.validFrom ?? '…'} — ${rule.validUntil ?? '…'}`}</small></button>)}</div>}
    </div>
    <aside className="schedule-inspector">
      <p className="eyebrow">НАЛАШТУВАННЯ ПРОМІЖКУ</p>
      {selectedRule === null ? <p className="inspector-empty">Оберіть проміжок у списку або створіть новий.</p> : <>
        <h2>{selectedRule.programId === null ? matrixName(selectedRule.clockTemplateId) : programName(selectedRule.programId)}</h2>
        <label>Що має звучати<select value={selectedRule.programId === null ? `matrix:${selectedRule.clockTemplateId ?? ''}` : `program:${selectedRule.programId}`} onChange={(event) => onSetSource(event.target.value)}><optgroup label="Матриці плейлистів">{configuration.clockTemplates.filter((matrix) => !configuration.programs.some((program) => program.defaultClockTemplateId === matrix.id)).map((matrix) => <option key={matrix.id} value={`matrix:${matrix.id}`}>{matrix.name}</option>)}</optgroup>{configuration.programs.length > 0 && <optgroup label="Готові плейлисти програм і реклами">{configuration.programs.map((program) => <option key={program.id} value={`program:${program.id}`}>{program.name}</option>)}</optgroup>}</select></label>
        <div className="time-pair"><label>Від<input type="time" step="60" value={shortTime(selectedRule.startLocal)} onChange={(event) => onUpdate({ startLocal: `${event.target.value}:00` })} /></label><label>До<input type="time" step="60" value={shortTime(selectedRule.endLocal)} onChange={(event) => onUpdate({ endLocal: `${event.target.value}:00` })} /></label></div>
        <span className="field-title">Дні тижня</span><div className="weekday-picker">{weekdays.map(([number, label]) => <button key={number} type="button" className={selectedRule.weekdays.includes(number) ? 'active' : ''} onClick={() => onUpdate({ weekdays: selectedRule.weekdays.includes(number) ? selectedRule.weekdays.filter((day) => day !== number) : [...selectedRule.weekdays, number].sort() })}>{label}</button>)}</div>
        <div className="date-pair"><label>Діє від <small>необов’язково</small><input type="date" value={selectedRule.validFrom ?? ''} onChange={(event) => onUpdate({ validFrom: event.target.value || null })} /></label><label>Діє до <small>необов’язково</small><input type="date" value={selectedRule.validUntil ?? ''} onChange={(event) => onUpdate({ validUntil: event.target.value || null })} /></label></div>
        <label>Пріоритет<input type="number" min="-999" max="1000" value={selectedRule.priority} onChange={(event) => onUpdate({ priority: Number(event.target.value) })} /><small>Якщо проміжки перетинаються, працює той, у якого більше число.</small></label>
        <label className="toggle"><input type="checkbox" checked={selectedRule.enabled} onChange={(event) => onUpdate({ enabled: event.target.checked })} /><span />Проміжок активний</label>
        <p className="schedule-policy">Перехід відбудеться після завершення поточного матеріалу. Планувальник заздалегідь намагатиметься мінімізувати відхилення від зазначеного часу.</p>
        <button className="danger-button" type="button" onClick={onRemove}>Видалити проміжок</button>
      </>}
    </aside>
  </section>;
}

function MatricesView({ configuration, tracks, baseId, selectedMatrix, selectedItem, selectedCategory, programByMatrix, onSelectMatrix, onSelectItem, onSelectCategory, onAddMatrix, onAddProgram, onAddCategory, onAddItem, onUpdateMatrix, onUpdateItem, onChangeItemType, onUpdateCategory, onMoveItem, onRemoveItem }: { configuration: SchedulerConfiguration; tracks: TrackSummary[]; baseId: string | null; selectedMatrix: ClockTemplate | null; selectedItem: ClockSlot | null; selectedCategory: RotationCategory | null; programByMatrix: Map<string, Program>; onSelectMatrix: (id: string) => void; onSelectItem: (id: string) => void; onSelectCategory: (id: string) => void; onAddMatrix: () => void; onAddProgram: () => void; onAddCategory: () => void; onAddItem: () => void; onUpdateMatrix: (update: (matrix: ClockTemplate) => ClockTemplate) => void; onUpdateItem: (patch: Partial<ClockSlot>) => void; onChangeItemType: (type: ClockSlotType) => void; onUpdateCategory: (patch: Partial<RotationCategory>) => void; onMoveItem: (direction: -1 | 1) => void; onRemoveItem: () => void }): ReactElement {
  const isProgram = selectedMatrix !== null && programByMatrix.has(selectedMatrix.id);
  return <section className="matrix-layout">
    <aside className="matrix-list-pane">
      <div className="pane-title"><div><p className="eyebrow">АВТОМАТИЧНИЙ ДОБІР</p><h2>Матриці</h2></div><button type="button" onClick={onAddMatrix}>+</button></div>
      {configuration.clockTemplates.filter((matrix) => !programByMatrix.has(matrix.id)).map((matrix) => <button key={matrix.id} type="button" className={matrix.id === selectedMatrix?.id ? 'selected' : ''} onClick={() => onSelectMatrix(matrix.id)}><strong>{matrix.name}</strong><span>{matrix.id === baseId ? 'БАЗОВА' : `${matrix.slots.length} позицій`}</span></button>)}
      <div className="pane-title programme-title"><div><p className="eyebrow">ГОТОВИЙ ПОРЯДОК</p><h2>Програми й реклама</h2></div><button type="button" onClick={onAddProgram}>+</button></div>
      {configuration.clockTemplates.filter((matrix) => programByMatrix.has(matrix.id)).map((matrix) => <button key={matrix.id} type="button" className={matrix.id === selectedMatrix?.id ? 'selected' : ''} onClick={() => onSelectMatrix(matrix.id)}><strong>{programByMatrix.get(matrix.id)?.name ?? matrix.name}</strong><span>{matrix.slots.length} матеріалів</span></button>)}
    </aside>

    <div className="matrix-editor">
      {selectedMatrix === null ? <p className="schedule-empty">Оберіть або створіть матрицю.</p> : <>
        <header className="matrix-editor-heading"><div><p className="eyebrow">{isProgram ? 'ГОТОВИЙ ПЛЕЙЛИСТ' : selectedMatrix.id === baseId ? 'БАЗОВА МАТРИЦЯ' : 'МАТРИЦЯ ПЛЕЙЛИСТІВ'}</p><input aria-label="Назва матриці" value={selectedMatrix.name} onChange={(event) => onUpdateMatrix((matrix) => ({ ...matrix, name: event.target.value }))} /></div><button className="button secondary" type="button" onClick={onAddItem}>+ Додати позицію</button></header>
        <p className="matrix-explainer">{isProgram ? 'Матеріали прозвучать у вказаному порядку. Такий плейлист можна вставити в розклад як програму, проєкт або рекламний блок.' : 'Система послідовно проходить ці позиції й для кожної добирає матеріал за правилами відповідної ротації.'}</p>
        {selectedMatrix.slots.length === 0 ? <p className="matrix-empty">Матриця порожня. Додайте першу позицію.</p> : <ol className="matrix-sequence">{normalizeClockSlots(selectedMatrix.slots).map((item, index) => <li key={item.id}><button type="button" className={item.id === selectedItem?.id ? 'selected' : ''} onClick={() => onSelectItem(item.id)}><span className="sequence-number">{String(index + 1).padStart(2, '0')}</span><span className={`kind-label kind-${item.slotType.toLowerCase()}`}>{kindLabel(item.slotType)}</span><strong>{item.slotType === 'ROTATION' ? configuration.rotationCategories.find((category) => category.id === item.rotationCategoryId)?.name ?? 'Категорію не вибрано' : item.slotType === 'ITEM' ? trackLabel(tracks.find((track) => track.id === item.fixedTrackId)) : 'Будь-який доступний матеріал'}</strong></button></li>)}</ol>}
      </>}
    </div>

    <aside className="matrix-inspector">
      <p className="eyebrow">ПОЗИЦІЯ МАТРИЦІ</p>
      {selectedItem === null ? <p className="inspector-empty">Оберіть позицію у послідовності.</p> : <>
        <h2>Позиція {selectedItem.position + 1}</h2>
        {!isProgram && <label>Тип<select value={selectedItem.slotType} onChange={(event) => onChangeItemType(event.target.value as ClockSlotType)}><option value="ROTATION">Ротація</option><option value="ITEM">Конкретний матеріал</option><option value="FLEX">Будь-який доступний</option></select></label>}
        {selectedItem.slotType === 'ROTATION' && <label>Ротація<select value={selectedItem.rotationCategoryId ?? ''} onChange={(event) => onUpdateItem({ rotationCategoryId: event.target.value || null })}><option value="">Оберіть…</option>{configuration.rotationCategories.map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}</select></label>}
        {(selectedItem.slotType === 'ITEM' || isProgram) && <label>Матеріал<select value={selectedItem.fixedTrackId ?? ''} onChange={(event) => onUpdateItem({ slotType: 'ITEM', fixedTrackId: event.target.value || null, rotationCategoryId: null })}><option value="">Оберіть матеріал…</option>{tracks.map((track) => <option key={track.id} value={track.id}>{trackOptionLabel(track)}</option>)}</select><small>{tracks.length === 0 ? 'Спочатку додайте аудіофайли до медіатеки.' : 'Показано назву, виконавця, тип і тривалість.'}</small></label>}
        <label>Профіль переходу <small>необов’язково</small><input className="mono" value={selectedItem.transitionProfileId ?? ''} onChange={(event) => onUpdateItem({ transitionProfileId: event.target.value || null })} placeholder="За замовчуванням" /></label>
        <div className="sequence-actions"><button type="button" onClick={() => onMoveItem(-1)}>Підняти</button><button type="button" onClick={() => onMoveItem(1)}>Опустити</button></div>
        <button className="danger-button" type="button" onClick={onRemoveItem}>Видалити позицію</button>
      </>}

      {!isProgram && <RotationEditor configuration={configuration} tracks={tracks} selected={selectedCategory} onSelect={onSelectCategory} onAdd={onAddCategory} onUpdate={onUpdateCategory} />}
    </aside>
  </section>;
}

function RotationEditor({ configuration, tracks, selected, onSelect, onAdd, onUpdate }: { configuration: SchedulerConfiguration; tracks: TrackSummary[]; selected: RotationCategory | null; onSelect: (id: string) => void; onAdd: () => void; onUpdate: (patch: Partial<RotationCategory>) => void }): ReactElement {
  const matches = selected === null ? [] : tracks.filter((track) => matchesRotation(track, selected));
  const updateCriteria = (patch: Partial<RotationCategory['criteria']>): void => {
    if (selected !== null) onUpdate({ criteria: { ...selected.criteria, ...patch } });
  };
  return <section className="rotation-editor">
    <div className="pane-title"><div><p className="eyebrow">ПРАВИЛА ДОБОРУ</p><h2>Ротації</h2></div><button type="button" onClick={onAdd}>+</button></div>
    <p className="rotation-help">Ротація сама добирає дозволені матеріали з медіатеки. Змінили правило тут — не треба відкривати кожну пісню.</p>
    <div className="rotation-tabs">{configuration.rotationCategories.map((category) => <button key={category.id} type="button" className={category.id === selected?.id ? 'active' : ''} onClick={() => onSelect(category.id)}>{category.name}</button>)}</div>
    {selected !== null && <div className="rotation-fields">
      <label>Назва<input value={selected.name} onChange={(event) => onUpdate({ name: event.target.value })} /></label>
      <fieldset className="rotation-types"><legend>Типи матеріалів</legend>{(['TRACK', 'JINGLE', 'SWEEPER', 'LINER', 'STINGER', 'BED', 'PROMO'] as TrackSummary['itemType'][]).map((type) => <label key={type}><input type="checkbox" checked={selected.criteria.itemTypes.includes(type)} onChange={() => updateCriteria({ itemTypes: toggleValue(selected.criteria.itemTypes, type) })} />{itemTypeLabel(type)}</label>)}</fieldset>
      <label>Виконавці <small>через кому; порожньо — будь-які</small><input value={selected.criteria.artists.join(', ')} onChange={(event) => updateCriteria({ artists: splitCriteria(event.target.value) })} placeholder="наприклад, ONUKA, The Hardkiss" /></label>
      <label>Жанри <small>через кому; порожньо — будь-які</small><input value={selected.criteria.genres.join(', ')} onChange={(event) => updateCriteria({ genres: splitCriteria(event.target.value) })} placeholder="наприклад, pop, rock" /></label>
      <label>Мови <small>через кому; порожньо — будь-які</small><input value={selected.criteria.languages.join(', ')} onChange={(event) => updateCriteria({ languages: splitCriteria(event.target.value) })} placeholder="наприклад, uk, en" /></label>
      <div className="separation-fields"><label>Темп від, BPM<input type="number" min="1" max="400" value={selected.criteria.bpmMin ?? ''} onChange={(event) => updateCriteria({ bpmMin: nullableNumeric(event.target.value) })} /></label><label>Темп до, BPM<input type="number" min="1" max="400" value={selected.criteria.bpmMax ?? ''} onChange={(event) => updateCriteria({ bpmMax: nullableNumeric(event.target.value) })} /></label></div>
      <div className={`rotation-match-summary ${matches.length === 0 ? 'empty' : ''}`}><strong>{matches.length} матеріалів відповідають правилам</strong>{matches.length === 0 ? <small>Ця ротація не зможе сформувати ефір. Послабте фільтри або підготуйте матеріали.</small> : <small>{matches.slice(0, 3).map(trackLabel).join(' · ')}</small>}</div>
      <div className="separation-fields"><label><span>Трек не повторювати раніше ніж через, хв</span><input type="number" min="0" step="1" aria-describedby="track-separation-help" value={selected.minTrackSeparationSeconds / 60} onChange={(event) => onUpdate({ minTrackSeparationSeconds: Math.max(0, Number(event.target.value) * 60) })} /><small id="track-separation-help">{separationExplanation('track', selected.minTrackSeparationSeconds / 60)}</small></label><label><span>Виконавця не повторювати раніше ніж через, хв</span><input type="number" min="0" step="1" aria-describedby="artist-separation-help" value={selected.minArtistSeparationSeconds / 60} onChange={(event) => onUpdate({ minArtistSeparationSeconds: Math.max(0, Number(event.target.value) * 60) })} /><small id="artist-separation-help">{separationExplanation('artist', selected.minArtistSeparationSeconds / 60)}</small></label></div>
      <p className="separation-policy"><strong>Важливо:</strong> якщо придатних альтернатив немає, система спочатку послаблює інтервал виконавця, а інтервал конкретного треку — лише останнім кроком. Це не дає ефіру зупинитися.</p>
      <label className="toggle"><input type="checkbox" checked={selected.enabled} onChange={(event) => onUpdate({ enabled: event.target.checked })} /><span />Ротація активна</label>
    </div>}
  </section>;
}

function QueueView({ future, tracks, stationId, manualTrackId, manualAt, insertMode, exactTolerance, onManualTrackId, onManualAt, onInsertMode, onExactTolerance, onSubmit }: { future: FutureSchedule | null; tracks: TrackSummary[]; stationId: string; manualTrackId: string; manualAt: string; insertMode: 'manual' | 'exact'; exactTolerance: number; onManualTrackId: (value: string) => void; onManualAt: (value: string) => void; onInsertMode: (value: 'manual' | 'exact') => void; onExactTolerance: (value: number) => void; onSubmit: (event: FormEvent) => void }): ReactElement {
  const lockUntil = Date.now() + 15 * 60_000;
  return <section className="queue-workspace">
    <header className="section-heading"><div><p className="eyebrow">ФАКТИЧНО СФОРМОВАНИЙ ЕФІР</p><h2>Черга на 3 години</h2><p>Найближчі 15 хвилин зафіксовані. Решту черги система може оптимізувати під майбутні програми й рекламу.</p></div><div className="queue-legend"><span className="locked">ЗАБЛОКОВАНО</span><span>МОЖЕ ЗМІНИТИСЯ</span></div></header>
    {future === null || future.items.length === 0 ? <p className="schedule-empty">Черга ще не сформована. Після збереження налаштувань сервер створить її автоматично.</p> : <div className="future-table schedule-future-table" role="table" aria-label="Черга ефіру"><div className="future-table-head" role="row"><span>Початок</span><span>Стан</span><span>Матеріал</span><span>Тривалість</span><span>Джерело</span></div>{future.items.map((item) => { const locked = item.sourceKind === 'LOCKED_ITEM' || new Date(item.scheduledAt).getTime() < lockUntil; return <div className={`future-row ${locked ? 'locked-row' : ''}`} role="row" key={item.id}><time>{formatDateTime(item.scheduledAt)}</time><span className={`queue-lock ${locked ? 'locked' : ''}`}>{locked ? 'ЗАБЛОКОВАНО' : 'ПЛАН'}</span><span><strong>{item.title}</strong><small>{item.artist}</small></span><span className="mono">{formatDuration(item.expectedDurationMs)}</span><span>{sourceLabel(item.sourceKind)}</span></div>; })}</div>}
    <form className="schedule-event-form" onSubmit={onSubmit}><div><p className="eyebrow">РАЗОВА ПОДІЯ</p><h2>Додати матеріал у розклад</h2><p>Звичайна вставка дочекається завершення поточного матеріалу. Точний час використовуйте лише там, де допустимий примусовий перехід.</p></div><label>Режим<select value={insertMode} onChange={(event) => onInsertMode(event.target.value as 'manual' | 'exact')}><option value="manual">Після поточного матеріалу</option><option value="exact">Точно у визначений час</option></select></label><label>Матеріал<select required value={manualTrackId} onChange={(event) => onManualTrackId(event.target.value)}><option value="">Оберіть матеріал…</option>{tracks.map((track) => <option key={track.id} value={track.id}>{trackOptionLabel(track)}</option>)}</select><small>Не знайшли потрібний? <a href={`/admin/library?station=${stationId}`}>Відкрити медіатеку</a>.</small></label><label>Дата й час<input required type="datetime-local" value={manualAt} onChange={(event) => onManualAt(event.target.value)} /></label>{insertMode === 'exact' && <label>Допустиме відхилення, мс<input type="number" min="0" max="3600000" value={exactTolerance} onChange={(event) => onExactTolerance(Number(event.target.value))} /></label>}<button className="button primary" type="submit">Додати подію</button></form>
  </section>;
}

function EmptyState({ title, detail, action }: { title: string; detail: string; action?: ReactElement }): ReactElement {
  return <section className="empty-state"><p className="eyebrow">ПОЧАТКОВЕ НАЛАШТУВАННЯ</p><h2>{title}</h2><p>{detail}</p>{action}</section>;
}

function problemMessage(reason: unknown): string {
  if (reason instanceof ApiError) {
    if (reason.problem.status === 401) return 'Сесія завершилась. Увійдіть у платформу знову.';
    if (reason.problem.status === 409) return 'Конфігурацію вже змінив інший оператор. Оновіть сторінку перед повторним редагуванням.';
    return reason.problem.detail ?? reason.problem.title;
  }
  return reason instanceof Error ? reason.message : 'Невідома помилка планувальника.';
}

function kindLabel(kind: ClockSlotType): string {
  return ({ ROTATION: 'Ротація', ITEM: 'Матеріал', FLEX: 'Автодобір', MARKER: 'Мітка' })[kind];
}

function sourceLabel(source: string): string {
  return ({ CLOCK_ROTATION: 'Матриця', CLOCK_ITEM: 'Готовий плейлист', CLOCK_FLEX: 'Автодобір', EXACT_EVENT: 'Точна подія', MANUAL_INSERT: 'Ручна вставка', LOCKED_ITEM: 'Збережено з попереднього плану' } as Record<string, string>)[source] ?? source;
}

function shortTime(value: string): string {
  return value.slice(0, 5);
}

function weekdayLabel(days: number[]): string {
  if (days.length === 7) return 'Щодня';
  if (days.join(',') === '1,2,3,4,5') return 'Будні';
  if (days.join(',') === '6,7') return 'Вихідні';
  return weekdays.filter(([number]) => days.includes(number)).map(([, label]) => label).join(', ');
}

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat('uk-UA', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }).format(new Date(value));
}

function formatDuration(milliseconds: number): string {
  const seconds = Math.round(milliseconds / 1000);
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

function trackLabel(track: TrackSummary | undefined): string {
  return track === undefined ? 'Матеріал не вибрано' : `${track.artist || 'Без виконавця'} — ${track.title || 'Без назви'}`;
}

function trackOptionLabel(track: TrackSummary): string {
  return `${trackLabel(track)} · ${itemTypeLabel(track.itemType)} · ${formatDuration(track.durationMs)}`;
}

function itemTypeLabel(type: TrackSummary['itemType']): string {
  return ({
    TRACK: 'Пісня',
    JINGLE: 'Джингл',
    SWEEPER: 'Свіпер',
    LINER: 'Лайнер',
    STINGER: 'Стінгер',
    BED: 'Підклад',
    PROMO: 'Промо',
    ADVERTISEMENT: 'Реклама',
    VOICE_TRACK: 'Голосовий трек',
    PROGRAM_ITEM: 'Програма',
  })[type];
}

function toggleValue(values: string[], value: string): string[] {
  return values.includes(value) ? values.filter((item) => item !== value) : [...values, value];
}

function splitCriteria(value: string): string[] {
  return [...new Set(value.split(',').map((item) => item.trim()).filter(Boolean))];
}

function nullableNumeric(value: string): number | null {
  return value.trim() === '' ? null : Number(value);
}

function matchesRotation(track: TrackSummary, category: RotationCategory): boolean {
  const criteria = category.criteria;
  const normalized = (value: string | null): string => (value ?? '').trim().toLocaleLowerCase('uk-UA');
  const includes = (values: string[], value: string | null): boolean => values.length === 0 || values.map(normalized).includes(normalized(value));
  return (criteria.itemTypes.length === 0 || criteria.itemTypes.includes(track.itemType))
    && includes(criteria.artists, track.artist)
    && includes(criteria.genres, track.genre)
    && includes(criteria.languages, track.language)
    && (criteria.bpmMin === null || (track.bpm !== null && track.bpm >= criteria.bpmMin))
    && (criteria.bpmMax === null || (track.bpm !== null && track.bpm <= criteria.bpmMax));
}
